import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadMore,
  LoadingBlock,
  PageHeader,
  StatusBadge,
  Table,
  Tag,
  Td,
  Th,
} from "../components/ui";
import { formatCountdown, formatDate } from "../lib/format";
import { formatMoney } from "../lib/money";
import { COMMISSION_STATES, REFERRAL_STATES } from "../lib/states";
import { useCommissions, useReferrals } from "../lib/queries";
import { useTranslation } from "react-i18next";

/**
 * Two tables, and they answer two different questions.
 *
 * Referrals are "who arrived", commissions are "what that was worth". Merging
 * them looks tidier and is wrong: a referral with no commission is the normal
 * state for most of them, and a merged table would show a column of blanks that
 * reads as missing data rather than as a customer who has not paid yet.
 *
 * Neither table names anybody. There is no account id, no email, no country and
 * no top-up amount on either — an affiliate is owed a percentage and is
 * entitled to know the percentage and what it came to, and what a customer
 * chose to spend is that customer's business. The API does not send those
 * fields at all, so this is a property of the boundary rather than of the
 * markup.
 */
export function ReferralsPage() {
  const { t } = useTranslation("referrals");
  const { t: ts } = useTranslation("states");
  const referrals = useReferrals();
  const commissions = useCommissions();

  /*
   * The pages flattened once, here, rather than at each place they are read.
   * `useInfiniteQuery` hands back `{ pages: [{ data, nextCursor }] }`, and a
   * component that reaches into that shape is one that has to change when the
   * paging does.
   */
  const referralRows = referrals.data?.pages.flatMap((page) => page.data) ?? [];
  const commissionRows = commissions.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
      />

      <Card>
        <CardHeader title={t("signups")} />
        {referrals.isPending ? (
          <LoadingBlock />
        ) : referrals.error ? (
          <div className="px-4 py-4">
            <Alert>{referrals.error.message}</Alert>
          </div>
        ) : referralRows.length === 0 ? (
          <EmptyState
            title={t("noSignups")}
            description={t("noSignupsDescription")}
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("th.signedUp")}</Th>
                <Th priority="sm">{t("th.link")}</Th>
                <Th>{t("th.status")}</Th>
                <Th className="text-right">Commission</Th>
              </tr>
            }
          >
            {referralRows.map((referral) => {
              const state = REFERRAL_STATES[referral.status];
              return (
                <tr key={referral.id}>
                  <Td>{formatDate(referral.signedUpAt)}</Td>
                  <Td priority="sm">
                    <Tag>{referral.linkCode}</Tag>
                  </Td>
                  <Td>
                    <div className="space-y-0.5">
                      <StatusBadge tone={state.tone} label={ts(state.label)} />
                      {state.hint && (
                        <p className="text-[12px] text-fg-subtle">{ts(state.hint)}</p>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {referral.commissionMicros === null ? (
                      <span className="text-fg-subtle">—</span>
                    ) : (
                      formatMoney(referral.commissionMicros)
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
        <LoadMore
          hasMore={referrals.hasNextPage}
          loading={referrals.isFetchingNextPage}
          onMore={() => void referrals.fetchNextPage()}
          loaded={referralRows.length}
          noun="signups"
        />
      </Card>

      <Card>
        <CardHeader
          title={t("commissions")}
          description={t("commissionsDescription")}
        />
        {commissions.isPending ? (
          <LoadingBlock />
        ) : commissions.error ? (
          <div className="px-4 py-4">
            <Alert>{commissions.error.message}</Alert>
          </div>
        ) : commissionRows.length === 0 ? (
          <EmptyState
            title={t("nothingEarned")}
            description={t("nothingEarnedDescription")}
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("th.earned")}</Th>
                <Th priority="md">{t("th.link")}</Th>
                <Th>{t("th.status")}</Th>
                <Th className="text-right">{t("th.amount")}</Th>
              </tr>
            }
          >
            {commissionRows.map((commission) => {
              const state = COMMISSION_STATES[commission.status];
              return (
                <tr key={commission.id}>
                  <Td>{formatDate(commission.earnedAt)}</Td>
                  <Td priority="md">
                    <Tag>{commission.linkCode}</Tag>
                  </Td>
                  <Td>
                    <div className="space-y-0.5">
                      <StatusBadge tone={state.tone} label={ts(state.label)} />
                      {/*
                       * A held commission says *when* it clears rather than
                       * that it is held — the countdown is the question being
                       * asked, and "Held" alone invites somebody to ask support
                       * how long for.
                       *
                       * A reversed one says why. That is the one state on this
                       * dashboard somebody has to be told about rather than
                       * shown, because money they were told they had earned
                       * has gone.
                       */}
                      {commission.status === "pending" && commission.payableAt && (
                        <p className="text-[12px] text-fg-subtle">
                          {t("clears", { when: formatCountdown(commission.payableAt) })}
                        </p>
                      )}
                      {commission.resolutionReason && (
                        <p className="text-[12px] text-fg-subtle">
                          {commission.resolutionReason}
                        </p>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right tabular-nums">
                    <span
                      className={
                        commission.status === "reversed" ? "text-fg-subtle line-through" : undefined
                      }
                    >
                      {formatMoney(commission.amountMicros, commission.currency)}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
        <LoadMore
          hasMore={commissions.hasNextPage}
          loading={commissions.isFetchingNextPage}
          onMore={() => void commissions.fetchNextPage()}
          loaded={commissionRows.length}
          noun="commissions"
        />
      </Card>
    </>
  );
}
